import { defineMessages, type MessageDescriptor } from 'react-intl';

export type OfflineRecordingErrorSeverity = 'passive' | 'needs_action';

export interface OfflineRecordingErrorMessage {
  title: MessageDescriptor;
  description: MessageDescriptor;
  severity: OfflineRecordingErrorSeverity;
}

const messages = defineMessages({
  authRequiredTitle: {
    defaultMessage: 'Sign in again to finish syncing',
    id: 'i3ZOUjgqez',
  },
  authRequiredDescription: {
    defaultMessage:
      'Your session is saved on this device. Sign in again and Heidi will finish syncing it.',
    id: 'OHX96oqj2c',
  },
  uploadFailedTitle: {
    defaultMessage: 'Upload paused',
    id: 'HCFocK6db3',
  },
  uploadFailedDescription: {
    defaultMessage:
      'Your session is saved on this device. Heidi will keep retrying when the connection is ready.',
    id: '1LdpsMp9q+',
  },
  syncFailedTitle: {
    defaultMessage: 'Sync paused',
    id: 'mvkuyqL1/q',
  },
  syncFailedDescription: {
    defaultMessage:
      'Your session is saved on this device. Heidi will keep retrying in the background.',
    id: '7qHiqHhfBA',
  },
  localRecordingTitle: {
    defaultMessage: 'Session saved locally',
    id: '17heknh5pN',
  },
  localRecordingDescription: {
    defaultMessage:
      'The session is still on this device. Heidi will keep retrying when it can safely sync.',
    id: 'wx0T0zsSKM',
  },
  storageNeedsActionTitle: {
    defaultMessage: 'Check this device before transcribing offline',
    id: 'sLAjiUhSYj',
  },
  storageNeedsActionDescription: {
    defaultMessage:
      'Heidi could not safely save the offline session. Free up storage or sign in online before trying again.',
    id: 'wTOOOV9KCR',
  },
  unrecoverableTitle: {
    defaultMessage: "This session couldn't be recovered",
    id: 'o1GYIfbULD',
  },
  unrecoverableDescription: {
    defaultMessage:
      'The saved audio on this device is corrupted or missing. You can delete this local session.',
    id: '+x76EaSyTF',
  },
  deleteFailedTitle: {
    defaultMessage: "A session couldn't be deleted",
    id: 'H/OCAB9E3s',
  },
  deleteFailedDescription: {
    defaultMessage: "It's back in your list. Try deleting it again.",
    id: '4n7yoWZLES',
  },
  genericTitle: {
    defaultMessage: 'Offline sync paused',
    id: 'CNQaXYs3T/',
  },
  genericDescription: {
    defaultMessage:
      'Your session is saved on this device. Heidi will keep retrying when it can.',
    id: '6hvotthmpn',
  },
});

export const offlineRecordingErrorMessages = {
  authRequired: {
    title: messages.authRequiredTitle,
    description: messages.authRequiredDescription,
    severity: 'needs_action',
  },
  uploadFailed: {
    title: messages.uploadFailedTitle,
    description: messages.uploadFailedDescription,
    severity: 'passive',
  },
  syncFailed: {
    title: messages.syncFailedTitle,
    description: messages.syncFailedDescription,
    severity: 'passive',
  },
  localRecording: {
    title: messages.localRecordingTitle,
    description: messages.localRecordingDescription,
    severity: 'passive',
  },
  storageNeedsAction: {
    title: messages.storageNeedsActionTitle,
    description: messages.storageNeedsActionDescription,
    severity: 'needs_action',
  },
  unrecoverable: {
    title: messages.unrecoverableTitle,
    description: messages.unrecoverableDescription,
    severity: 'needs_action',
  },
  // The one key for a delete that has given up. Nothing above it can be
  // reused: every other entry either promises Heidi will keep retrying, or
  // talks about local audio being corrupt — both the opposite of a session
  // that is back in the list because the server refused to remove it.
  deleteFailed: {
    title: messages.deleteFailedTitle,
    description: messages.deleteFailedDescription,
    severity: 'needs_action',
  },
  generic: {
    title: messages.genericTitle,
    description: messages.genericDescription,
    severity: 'passive',
  },
} satisfies Record<string, OfflineRecordingErrorMessage>;

type OfflineRecordingErrorMessageKey = keyof typeof offlineRecordingErrorMessages;

// Covers the full OFFLINE_*/SESSION_* set from Rust — a superset of
// session_engine:task_failed (SESSION_* only). Do NOT mirror into
// resolveTaskFailedStatus; OFFLINE_* codes never reach that path.
const ERROR_CODE_TO_MESSAGE_KEY: Record<string, OfflineRecordingErrorMessageKey> = {
  SESSION_AUTH_MISSING: 'authRequired',
  SESSION_ENGINE_NOT_RUNNING: 'syncFailed',
  SESSION_ALREADY_RUNNING: 'syncFailed',
  SESSION_CREATION_FAILED: 'syncFailed',
  SESSION_UPLOAD_FAILED: 'uploadFailed',
  SESSION_NOTE_GENERATION_FAILED: 'syncFailed',
  SESSION_IO_ERROR: 'syncFailed',
  OFFLINE_NO_CACHED_IDENTITY: 'authRequired',
  OFFLINE_RECORD_NOT_FOUND: 'localRecording',
  OFFLINE_ALREADY_RECORDING: 'localRecording',
  OFFLINE_ALREADY_CAPTURED: 'localRecording',
  OFFLINE_EMPTY_RECORDING: 'localRecording',
  OFFLINE_ENCRYPTION_FAILED: 'storageNeedsAction',
  OFFLINE_DECRYPTION_FAILED: 'unrecoverable',
  OFFLINE_CIPHERTEXT_MALFORMED: 'unrecoverable',
  OFFLINE_KEY_STORAGE_FAILED: 'storageNeedsAction',
  OFFLINE_KEY_MALFORMED: 'storageNeedsAction',
  OFFLINE_REGION_UNRESOLVED: 'syncFailed',
  OFFLINE_SEGMENTATION_FAILED: 'unrecoverable',
  OFFLINE_DISK_FULL: 'storageNeedsAction',
  OFFLINE_DATABASE_ERROR: 'syncFailed',
  OFFLINE_IO_ERROR: 'unrecoverable',
  OFFLINE_NOT_OWNER: 'authRequired',
  // The four terminal outcomes of a queued session delete. A parity test in
  // `error-messages.test.ts` reads the Rust producer and fails if it grows a
  // code this map has not decided about. The clinician's action is identical in
  // all four — the session is back, try again online — so they share one key
  // rather than four near-identical ones.
  //
  // The notice that shows this copy is NOT gated on the offline-recording
  // umbrella flag, and the gate an earlier revision of this comment called for
  // was withdrawn deliberately. Queued deletes keep replaying after the flag is
  // turned off, so a gate here would let a session quietly reappear in the list
  // with nothing said about it — the exact failure this copy exists to prevent.
  // The rollback lever is upstream: with the flag off nothing new is ever
  // queued, so nothing new can settle. What is already owed is still owed.
  //
  // The notice is also pinned to `deleteFailed` at its call site rather than
  // resolved from the code on the event, so a retryable code arriving through a
  // proxy cannot swap it for copy promising a retry that is not coming.
  // Note OFFLINE_DELETE_REGION_UNRESOLVED is deliberately NOT the same code as
  // OFFLINE_REGION_UNRESOLVED above, which promises a retry that will not come.
  OFFLINE_DELETE_FORBIDDEN: 'deleteFailed',
  OFFLINE_DELETE_ATTEMPTS_EXHAUSTED: 'deleteFailed',
  OFFLINE_DELETE_REGION_UNRESOLVED: 'deleteFailed',
  OFFLINE_DELETE_EXPIRED: 'deleteFailed',
  // OFFLINE_DELETE_REJECTED / _TRANSPORT / _UNEXPECTED_STATUS get no row on
  // purpose: they are retryable, never settle a row, and never reach the
  // clinician. Falling through to `generic` keeps them from ever being read as
  // a delete that gave up.
};

export const getOfflineRecordingErrorMessage = (
  errorCode: string | null | undefined
): OfflineRecordingErrorMessage => {
  if (!errorCode) return offlineRecordingErrorMessages.generic;

  const key = ERROR_CODE_TO_MESSAGE_KEY[errorCode];
  return key ? offlineRecordingErrorMessages[key] : offlineRecordingErrorMessages.generic;
};
